import {
    Button,
    CircularProgress,
    Container,
    Grid,
    Typography,
    useMediaQuery,
} from '@mui/material';
import banner from '../assets/infoPage/banner.png';
import activity1 from '../assets/infoPage/activity1.png';
import activity2 from '../assets/infoPage/activity2.png';
import heart from '../assets/infoPage/heart.png';
import pencil from '../assets/infoPage/pencil.png';
import { Box, styled } from '@mui/system';
import { BarChart } from '../components/charts/BarChart';
import { statistics } from '../apis/apis';
import { useEffect, useState } from 'react';
import { ScatterGeoChart } from '../components/charts/ScatterGeoChart';

interface InfoPageProps {
    handleStart: () => void;
}

const InfoBox = styled('div')({
    padding: '1rem',
    borderRadius: '2rem',
    backgroundColor: 'rgba(157, 136, 136, 0.2)',
    '@media (max-width:480px)': {
        maxWidth: '100%',
    },
    maxWidth: 'min(50%, 30rem)',
    margin: '1rem',
});

const SectionHeading = styled(Typography)({
    fontWeight: 700,
    fontSize: ' 2.1rem',
    color: '#587a7f',
    textDecoration: 'underline',
    textDecorationThickness: '0.34rem',
    textUnderlineOffset: '0.5rem',
    padding: '1rem',
});

const SectionText = styled(Typography)({
    fontWeight: 600,
    fontSize: ' 1.2rem',
    color: '#404040',
    padding: '1rem',
});

const InfoChart = ({
    type,
    labels,
    datasets,
}: {
    type: 'bar' | 'map';
    labels: Array<string>;
    datasets: Array<number>;
}) => {
    const tooltipLabelFormatter = (tooltipItems: any) => {
        return `${tooltipItems.formattedValue} users`;
    };

    const formatData = [
        {
            label: labels[0],
            data: datasets,
            backgroundColor: '#587a7f',
        },
    ];

    switch (type) {
        case 'bar':
            return (
                <BarChart
                    labels={labels}
                    datasets={formatData}
                    tooltipFormatter={tooltipLabelFormatter}
                />
            );
        case 'map':
            return <ScatterGeoChart countries={labels} values={datasets} />;
        default:
            return <></>;
    }
};

interface InfoChartCardProps {
    labels: Array<string>;
    datasets: Array<number>;
    title: string;
    isLoading: boolean;
    type: 'bar' | 'map';
}

const InfoChartCard = ({
    labels,
    datasets,
    title,
    isLoading,
    type,
}: InfoChartCardProps) => {
    return (
        <Container>
            <Typography
                variant="h5"
                sx={{
                    color: 'white',
                    padding: '1rem',
                    textAlign: 'center',
                    backgroundColor: '#587a7f',
                    borderRadius: '1rem 1rem 0 0',
                    fontWeight: '600',
                }}
            >
                {title}
            </Typography>
            <Box
                sx={{
                    backgroundColor: 'rgba(215, 209, 209, 0.66)',
                    padding: '1rem',
                    borderRadius: '0 0 1rem 1rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: '10rem',
                    alignItems: 'center',
                }}
            >
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <InfoChart
                        type={type}
                        labels={labels}
                        datasets={datasets}
                    />
                )}
            </Box>
        </Container>
    );
};

const HeadingBox = styled('div')({});

export const InfoPage = ({ handleStart }: InfoPageProps) => {
    const mobile = useMediaQuery('(max-width:700px)');
    const [isLoading, setIsLoading] = useState(false);
    const [chartData, setChartData] = useState({
        userCount: 0,
        countries: {},
        firstLanguage: {},
    });

    const formatStatsResponse = (response: any) => {
        return {
            userCount: response?.userCount ?? 0,
            countries: response?.currentLocation ?? {},
            firstLanguage: response?.firstLanguages ?? {},
        };
    };

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            const response = await statistics();
            setChartData(formatStatsResponse(response));
            setIsLoading(false);
        }
        fetchData();
    }, []);

    return (
        <Container disableGutters maxWidth={false}>
            <Box sx={{ position: 'relative', maxWidth: 'unset' }}>
                <Box
                    sx={{
                        zIndex: '90',
                        position: 'absolute',
                        top: ['10%', '10%', '11%', '25%'],
                        left: ['19%', '40%', '36%', '40%', '45%'],
                        maxWidth: 'unset',
                        textDecoration: 'none',
                    }}
                >
                    <SectionHeading
                        sx={{
                            textDecoration: 'none',
                            padding: 0,
                            color: 'black',
                        }}
                    >
                        Teacher Voice
                    </SectionHeading>
                    <SectionText
                        sx={{
                            padding: 0,
                            maxWidth: '15rem',
                        }}
                    >
                        Identifying voice patterns in an educational setting
                    </SectionText>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: 'primary.dark',
                            margin: '1rem 2.5rem',
                        }}
                        onClick={handleStart}
                    >
                        Click to start
                    </Button>
                </Box>
                <Box
                    component="img"
                    src={banner}
                    width="1152"
                    height="235"
                    alt=""
                    sx={{
                        width: '100vw',
                        height: 'auto',
                        position: 'relative',
                        minWidth: '63rem',
                        minHeight: ['10rem', '16rem'],
                        left: ['-18rem', '-3rem', 'unset'],
                        filter: 'opacity(0.9)',
                    }}
                />
            </Box>
            <Container sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionHeading>What you will be doing</SectionHeading>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <img
                            src={activity1}
                            width="505"
                            height="235"
                            alt="audio wavelength"
                            style={{ width: '100%', height: 'auto' }}
                        ></img>
                        <SectionText
                            sx={{ textAlign: 'center', padding: '1rem' }}
                        >
                            Listen to some audio clips of teachers and tell us
                            which you find most engaging
                        </SectionText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <img
                            src={activity2}
                            width="505"
                            height="235"
                            alt="page with recording mic"
                            style={{ width: '100%', height: 'auto' }}
                        ></img>
                        <SectionText
                            sx={{ textAlign: 'center', padding: '1rem' }}
                        >
                            Record your very best teaching voice
                        </SectionText>
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionHeading sx={{ padding: '1rem' }} variant="h4">
                            Your impact
                        </SectionHeading>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                        }}
                    >
                        <img
                            src={pencil}
                            width="141"
                            height="141"
                            alt="pencil emoji"
                            style={{
                                width: '40%',
                                alignItems: 'center',
                                height: 'auto',
                            }}
                        ></img>
                        <SectionText
                            sx={{ textAlign: 'center', padding: '1rem' }}
                        >
                            Improving virtual teaching agents
                        </SectionText>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                        }}
                    >
                        <img
                            src={heart}
                            width="160"
                            height="160"
                            alt="heart emoji"
                            style={{
                                width: '40%',
                                alignItems: 'center',
                                height: 'auto',
                            }}
                        ></img>
                        <SectionText
                            sx={{ textAlign: 'center', padding: '1rem' }}
                        >
                            Helping to reduce burden on overworked teachers
                        </SectionText>
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionHeading>What’s public</SectionHeading>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                        }}
                    >
                        <InfoBox>
                            <SectionText
                                sx={{ textAlign: 'center', padding: '1rem' }}
                            >
                                The data will be stored on Amazon Web Services
                                (AWS) servers within Canada for up to 48 hours
                            </SectionText>
                        </InfoBox>
                        <InfoBox>
                            <SectionText
                                sx={{ textAlign: 'center', padding: '1rem' }}
                            >
                                For long term storage the data will be stored on
                                a Compute Canada server as well as in the
                                Federated Research Data Repository (FRDR) where
                                researchers will be able to apply to access the
                                data for research purposes
                            </SectionText>
                        </InfoBox>
                    </Grid>
                </Grid>

                <Grid container spacing={'1.5rem'}>
                    <Grid item xs={12}>
                        <SectionHeading>The statistics</SectionHeading>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{
                            marginLeft: '2rem',
                            borderRadius: '1rem',
                            textAlign: 'center',
                            padding: '1rem 1rem 1rem 0.85rem',
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: 'rgba(215, 209, 209, 0.66)',
                                minHeight: '45%',
                                minWidth: '9rem',
                                borderRadius: '1rem',
                                textAlign: 'center',
                                padding: '1rem 1rem 1rem 0.85rem',
                            }}
                        >
                            <SectionText
                                sx={{ padding: '1rem', color: '#587a7f' }}
                                variant="body1"
                            >
                                Number of Participants
                            </SectionText>
                            <Box
                                sx={{
                                    backgroundColor: '#C4C4C4',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    width: '15vw',
                                    height: '15vw',
                                    minHeight: '8rem',
                                    minWidth: '8rem',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    margin: 'auto',
                                    maxWidth: '12rem',
                                    maxHeight: '12rem',
                                    alignItems: 'center',
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress />
                                ) : (
                                    <>
                                        {' '}
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: '#587a7f',
                                            }}
                                            variant="h3"
                                        >
                                            {chartData.userCount}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: '#587a7f',
                                            }}
                                            variant="body1"
                                        >
                                            people
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </div>
                    </Grid>

                    <Grid item xs={12} sm={7}>
                        <Grid item xs={12}>
                            <InfoChartCard
                                title="Countries"
                                labels={Object.keys(chartData.countries)}
                                datasets={Object.values(chartData.countries)}
                                isLoading={isLoading}
                                type={'map'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <InfoChartCard
                                title="Languages"
                                labels={Object.keys(chartData.firstLanguage)}
                                datasets={Object.values(
                                    chartData.firstLanguage
                                )}
                                isLoading={isLoading}
                                type={'bar'}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </Container>
    );
};
